import { memo, useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react'
import QRCode from 'qrcode'
import { KeyRound, RadioTower, ShieldCheck } from 'lucide-react'
import { authApi } from '../../lib/authApi.js'
import { HttpError } from '../../lib/http.js'
import { useAuthReady, useAuthStatus, useAuthTrustedDeviceDays, useAuthUser, useStore } from '../../store/index.js'
import { Button } from '../ui/button.js'
import { Spinner } from '../common/Spinner.js'
import './AuthGate.css'

function errorMessage(error: unknown): string {
  if (error instanceof HttpError) {
    if (error.code === 'INVALID_CREDENTIALS') return 'Identifiant, mot de passe ou code invalide.'
    if (error.code === 'ACCOUNT_LOCKED') return 'Compte verrouillé pendant 15 minutes.'
    if (error.code === 'TOO_MANY_ATTEMPTS') return 'Trop de tentatives. Réessayez dans 15 minutes.'
    if (error.code === 'HTTPS_REQUIRED') return 'La connexion depuis le réseau local exige HTTPS.'
  }
  return error instanceof Error ? error.message : 'Une erreur est survenue.'
}

export const AuthGate = memo(function AuthGate({ children }: { children: ReactNode }) {
  const ready = useAuthReady()
  const status = useAuthStatus()
  const user = useAuthUser()
  const [serverUnavailable, setServerUnavailable] = useState(false)

  const refresh = useCallback(async () => {
    try {
      useStore.getState().setAuth(await authApi.me())
      setServerUnavailable(false)
    } catch (error) {
      if (error instanceof HttpError && error.status === 401) {
        setServerUnavailable(false)
        useStore.getState().clearAuth()
      } else {
        // Une panne serveur ne dit rien sur la validité de la session. Ne pas
        // transformer une erreur réseau en faux écran de déconnexion.
        setServerUnavailable(true)
      }
    }
  }, [])

  useEffect(() => {
    void refresh()
    const clear = () => {
      setServerUnavailable(false)
      useStore.getState().clearAuth()
    }
    window.addEventListener('trafficplay:auth-required', clear)
    return () => window.removeEventListener('trafficplay:auth-required', clear)
  }, [refresh])

  useEffect(() => {
    if (!serverUnavailable) return
    const retry = () => { void refresh() }
    const timer = setInterval(retry, 5_000)
    window.addEventListener('online', retry)
    return () => {
      clearInterval(timer)
      window.removeEventListener('online', retry)
    }
  }, [refresh, serverUnavailable])

  if (serverUnavailable) return (
    <AuthFrame title="Serveur TrafficPlay indisponible">
      <p className="auth-muted"><Spinner /> Reconnexion automatique en cours…</p>
      <Button type="button" onClick={() => void refresh()}>Réessayer maintenant</Button>
    </AuthFrame>
  )
  if (!ready) return <AuthFrame title="Ouverture de TrafficPlay"><p className="auth-muted"><Spinner /> Vérification de la session…</p></AuthFrame>
  if (!user || !status) return <LoginForm onAuthenticated={response => useStore.getState().setAuth(response)} />
  if (status === 'PASSWORD_CHANGE_REQUIRED') return <PasswordChangeForm onDone={refresh} />
  if (status === 'MFA_ENROLLMENT_REQUIRED') return <MfaEnrollment onDone={refresh} />
  if (status === 'MFA_CHALLENGE_REQUIRED') return <MfaChallenge onDone={refresh} />
  return <>{children}</>
})

const AuthFrame = memo(function AuthFrame({ title, children }: { title: string; children: ReactNode }) {
  return (
    <main className="auth-shell">
      <section className="auth-card">
        <div className="auth-brand"><RadioTower size={20} /><span>TRAFFICPLAY</span></div>
        <h1>{title}</h1>
        {children}
      </section>
    </main>
  )
})

const Field = memo(function Field(props: {
  label: string
  type?: string
  value: string
  onChange: (value: string) => void
  autoComplete?: string
  minLength?: number
  maxLength?: number
}) {
  return (
    <label className="auth-field">
      <span>{props.label}</span>
      <input
        className="tp-input"
        type={props.type ?? 'text'}
        value={props.value}
        onChange={event => props.onChange(event.target.value)}
        autoComplete={props.autoComplete}
        minLength={props.minLength}
        maxLength={props.maxLength}
        required
      />
    </label>
  )
})

const LoginForm = memo(function LoginForm({ onAuthenticated }: {
  onAuthenticated: (response: Awaited<ReturnType<typeof authApi.login>>) => void
}) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const submit = useCallback(async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true); setError('')
    try {
      onAuthenticated(await authApi.login(username, password))
    } catch (caught) {
      setError(errorMessage(caught))
    } finally {
      setBusy(false)
    }
  }, [onAuthenticated, password, username])
  return (
    <AuthFrame title="Connexion">
      <p className="auth-muted">Accédez à votre espace de travail TrafficPlay.</p>
      <form className="auth-form" onSubmit={submit}>
        <Field label="Identifiant" value={username} onChange={setUsername} autoComplete="username" />
        <Field label="Mot de passe" type="password" value={password} onChange={setPassword} autoComplete="current-password" />
        {error && <p className="auth-error" role="alert">{error}</p>}
        <Button type="submit" disabled={busy}>{busy ? 'Connexion…' : 'Se connecter'}</Button>
      </form>
    </AuthFrame>
  )
})

const PasswordChangeForm = memo(function PasswordChangeForm({ onDone }: { onDone: () => Promise<void> }) {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const submit = useCallback(async (event: FormEvent) => {
    event.preventDefault()
    if (newPassword !== confirmation) return setError('Les deux nouveaux mots de passe diffèrent.')
    setBusy(true); setError('')
    try {
      await authApi.changePassword(currentPassword, newPassword)
      await onDone()
    } catch (caught) {
      setError(errorMessage(caught))
    } finally {
      setBusy(false)
    }
  }, [confirmation, currentPassword, newPassword, onDone])
  return (
    <AuthFrame title="Choisir un nouveau mot de passe">
      <p className="auth-muted">Utilisez une phrase de passe de 15 à 128 caractères.</p>
      <form className="auth-form" onSubmit={submit}>
        <Field label="Mot de passe temporaire" type="password" value={currentPassword} onChange={setCurrentPassword} autoComplete="current-password" />
        <Field label="Nouveau mot de passe" type="password" value={newPassword} onChange={setNewPassword} minLength={12} maxLength={128} autoComplete="new-password" />
        <Field label="Confirmation" type="password" value={confirmation} onChange={setConfirmation} minLength={12} maxLength={128} autoComplete="new-password" />
        {error && <p className="auth-error" role="alert">{error}</p>}
        <Button type="submit" disabled={busy}>{busy ? 'Enregistrement…' : 'Changer le mot de passe'}</Button>
      </form>
    </AuthFrame>
  )
})

export const MfaEnrollment = memo(function MfaEnrollment({ onDone }: { onDone: () => Promise<void> }) {
  const [enrollment, setEnrollment] = useState<{ secret: string; otpauthUri: string } | null>(null)
  const [qr, setQr] = useState('')
  const [code, setCode] = useState('')
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null)
  const [error, setError] = useState('')
  useEffect(() => {
    authApi.startEnrollment().then(setEnrollment).catch(caught => setError(errorMessage(caught)))
  }, [])
  useEffect(() => {
    if (enrollment) void QRCode.toDataURL(enrollment.otpauthUri, { width: 220, margin: 1 }).then(setQr)
  }, [enrollment])
  const confirm = useCallback(async (event: FormEvent) => {
    event.preventDefault(); setError('')
    try {
      setRecoveryCodes((await authApi.confirmEnrollment(code.replace(/\s/g, ''))).recoveryCodes)
    } catch (caught) {
      setError(errorMessage(caught))
    }
  }, [code])
  if (recoveryCodes) {
    return (
      <AuthFrame title="Codes de récupération">
        <p className="auth-warning">Copiez ces dix codes maintenant. Ils ne seront plus affichés.</p>
        <div className="auth-recovery-codes">{recoveryCodes.map(value => <code key={value}>{value}</code>)}</div>
        <Button onClick={() => void onDone()}>J’ai sauvegardé mes codes</Button>
      </AuthFrame>
    )
  }
  return (
    <AuthFrame title="Sécuriser le compte administrateur">
      <div className="auth-security-heading"><ShieldCheck size={20} /><span>Le double facteur est obligatoire.</span></div>
      {qr && <img className="auth-qr" src={qr} alt="QR code TOTP TrafficPlay" />}
      {enrollment && <div className="auth-secret"><span>Clé manuelle</span><code>{enrollment.secret}</code></div>}
      <form className="auth-form" onSubmit={confirm}>
        <Field label="Code à 6 chiffres" value={code} onChange={setCode} autoComplete="one-time-code" />
        {error && <p className="auth-error" role="alert">{error}</p>}
        <Button type="submit" disabled={!enrollment}>Activer le double facteur</Button>
      </form>
    </AuthFrame>
  )
})

const MfaChallenge = memo(function MfaChallenge({ onDone }: { onDone: () => Promise<void> }) {
  const trustedDeviceDays = useAuthTrustedDeviceDays()
  const [recovery, setRecovery] = useState(false)
  const [value, setValue] = useState('')
  const [trustDevice, setTrustDevice] = useState(true)
  const [error, setError] = useState('')
  const submit = useCallback(async (event: FormEvent) => {
    event.preventDefault(); setError('')
    const trust = trustedDeviceDays > 0 && trustDevice
    try {
      await authApi.verifyMfa(recovery
        ? { recoveryCode: value.trim(), trustDevice: trust }
        : { code: value.replace(/\s/g, ''), trustDevice: trust })
      await onDone()
    } catch (caught) {
      setError(errorMessage(caught))
    }
  }, [onDone, recovery, trustDevice, trustedDeviceDays, value])
  return (
    <AuthFrame title="Vérification en deux étapes">
      <div className="auth-security-heading"><KeyRound size={20} /><span>{recovery ? 'Saisissez un code de récupération.' : 'Saisissez le code de votre application.'}</span></div>
      <form className="auth-form" onSubmit={submit}>
        <Field label={recovery ? 'Code de récupération' : 'Code à 6 chiffres'} value={value} onChange={setValue} autoComplete="one-time-code" />
        {trustedDeviceDays > 0 && (
          <label className="auth-trust">
            <input type="checkbox" checked={trustDevice} onChange={event => setTrustDevice(event.target.checked)} />
            <span>Ne plus demander de code sur cet appareil pendant {trustedDeviceDays}{'\u00a0'}jours</span>
          </label>
        )}
        {error && <p className="auth-error" role="alert">{error}</p>}
        <Button type="submit">Vérifier</Button>
        <button className="auth-link" type="button" onClick={() => { setRecovery(current => !current); setValue(''); setError('') }}>
          {recovery ? 'Utiliser l’application TOTP' : 'Utiliser un code de récupération'}
        </button>
      </form>
    </AuthFrame>
  )
})
